// ============================================================
// 天水數位店長 — 響應式導覽（依裝置切換）
//
// 手機 (<768px)：固定底部 Tab 列 + 「更多」抽屜
//   店裡員工幾乎都用手機打卡，拇指可及的底部列最好按。
//
// 平板／電腦 (≥768px)：左側固定側欄，全部功能一次列出
//   老闆在 iPad 或電腦上排班、看報表，底部小 Tab 既難點也浪費螢幕；
//   側欄可以一次顯示全部項目，不用再開「更多」。
//
// 配色依 CIS：主色 #698FB2（sage-500）用於選中狀態。
// ============================================================

import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { BrandMark } from './BrandMark';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  roles?: ('OWNER' | 'MANAGER' | 'STAFF')[];
  badge?: number;
}

export function Navigation({ pendingCount = 0 }: { pendingCount?: number }) {
  const { user, tenant, logout } = useAuthStore();
  const navigate = useNavigate();
  const [moreMenu, setMoreMenu] = useState(false);

  if (!user) return null;

  // 主要 tab（員工與管理者依角色顯示不同主選單）
  const primaryItems: NavItem[] = user.role === 'STAFF'
    ? [
        { path: '/my', label: '首頁', icon: '🏠' },
        { path: '/clock', label: '打卡', icon: '◉' },
        { path: '/leaves', label: '請假', icon: '✉' },
        { path: '/my-schedule', label: '我的班表', icon: '▦' },
      ]
    : [
        { path: '/dashboard', label: '總覽', icon: '▣', roles: ['OWNER', 'MANAGER'] },
        { path: '/schedule', label: '排班', icon: '▦', roles: ['OWNER', 'MANAGER'] },
        { path: '/clock', label: '打卡', icon: '◉' },
        { path: '/approvals', label: '簽核', icon: '✓', roles: ['OWNER', 'MANAGER'], badge: pendingCount },
        { path: '/leaves', label: '請假', icon: '✉' },
      ];

  // 「更多」抽屜裡的項目
  const moreItems: NavItem[] = user.role === 'STAFF'
    ? [
        { path: '/my-payroll', label: '我的薪資', icon: '💰' },
        { path: '/my-attendance', label: '出勤紀錄', icon: '🕘' },
        { path: '/comp-leave', label: '加班補休', icon: '🔄' },
        { path: '/attendance-correction', label: '補打卡', icon: '✏' },
        { path: '/password', label: '密碼與安全', icon: '🔒' },
      ]
    : [
        { path: '/staff', label: '員工列表', icon: '☷' },
        { path: '/attendance-report', label: '出勤月報', icon: '📅' },
        { path: '/revenue-report', label: '門店報表', icon: '📊' },
        { path: '/leave-overview', label: '休假總覽', icon: '🗓' },
        { path: '/payroll', label: '薪資結算', icon: '💰' },
        { path: '/shift-templates', label: '班別管理', icon: '🕒' },
        { path: '/holidays', label: '行事曆', icon: '📆' },
        { path: '/kiosk-qr', label: '打卡 QR', icon: '⊞' },
        { path: '/announcements', label: '公告管理', icon: '📢' },
        { path: '/settings', label: '門市設定', icon: '⚙' },
        { path: '/password', label: '密碼與安全', icon: '🔒' },
      ];

  const visibleItems = primaryItems.filter(
    (item) => !item.roles || item.roles.includes(user.role)
  );

  const handleLogout = () => {
    logout();
    navigate('/pin-login');
  };

  const go = (path: string) => {
    setMoreMenu(false);
    navigate(path);
  };

  // 側欄（平板／電腦）一次列出全部項目，不需要「更多」
  const allItems = [...visibleItems, ...moreItems];

  return (
    <>
      {/* ─── 平板／電腦：左側固定側欄 ─── */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 z-50 w-56 lg:w-64 flex-col bg-white border-r border-morandi-200">
        {/* 品牌區 */}
        <div className="px-5 py-5 border-b border-morandi-100">
          <div className="flex items-center gap-3">
            <BrandMark variant="symbol" tone="black" height={22} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-morandi-800 truncate">{tenant?.name ?? '天水豆腐店'}</div>
              <div className="text-xs text-morandi-400">數位店長</div>
            </div>
          </div>
        </div>

        {/* 功能列表 */}
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {allItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-btn text-sm transition-colors relative
                ${isActive
                  ? 'bg-sage-50 text-sage-700 font-medium'
                  : 'text-morandi-500 hover:bg-morandi-50 hover:text-morandi-700'}
              `}
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-sage-500" />}
                  <span className="text-base leading-none w-5 text-center">{item.icon}</span>
                  <span className="truncate">{item.label}</span>
                  {item.badge != null && item.badge > 0 && (
                    <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-rose-400 text-white text-xs rounded-full flex items-center justify-center font-mono">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* 使用者 + 登出 */}
        <div className="border-t border-morandi-100 p-3">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-sage-100 flex items-center justify-center text-sage-700 text-xs font-medium shrink-0">
              {user.name.slice(0, 1)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-morandi-700 truncate">{user.name}</div>
              <div className="text-xs text-morandi-400">
                {user.role === 'OWNER' ? '老闆' : user.role === 'MANAGER' ? '主管' : '員工'}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="mt-1 w-full py-2 rounded-btn text-sm text-rose-500 hover:bg-rose-50 transition-colors"
          >
            登出
          </button>
        </div>
      </aside>

      {/* ─── 手機：更多抽屜 ─── */}
      {moreMenu && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/30 flex items-end"
          onClick={() => setMoreMenu(false)}
        >
          <div
            className="w-full bg-white rounded-t-2xl p-5 shadow-2xl animate-slide-up max-h-[85vh] overflow-y-auto"
            style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-morandi-200 rounded-full mx-auto mb-4" />
            <h3 className="text-sm font-semibold text-morandi-700 mb-3">更多功能</h3>
            <div className="grid grid-cols-4 gap-3">
              {moreItems.map(item => (
                <button
                  key={item.path}
                  onClick={() => go(item.path)}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-morandi-100 transition-colors"
                >
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-xs text-morandi-600">{item.label}</span>
                </button>
              ))}
            </div>
            {/* 登出（手機底部 nav 擠不下，收進這裡） */}
            <button
              onClick={() => { setMoreMenu(false); handleLogout(); }}
              className="mt-5 w-full py-3 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-500 text-sm font-medium transition-colors"
            >
              登出
            </button>
          </div>
        </div>
      )}

      {/* ─── 手機：底部 Tab 列（md 以上改用側欄）─── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-morandi-200 shadow-card safe-area-bottom">
        <div className="flex items-center justify-between px-2 sm:px-4 gap-1">
          {/* 店鋪名 + 使用者（手機隱藏，避免擠掉 tab） */}
          <div className="hidden sm:flex items-center gap-2 py-2 min-w-[140px] flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-earth-200 flex items-center justify-center text-earth-600 text-xs font-medium">
              {user.name.slice(0, 1)}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-morandi-700 truncate">{user.name}</div>
              <div className="text-xs text-morandi-300 truncate">{tenant?.name}</div>
            </div>
          </div>

          {/* Tab Buttons */}
          <div className="flex items-center gap-0.5 sm:gap-1 flex-1 sm:flex-initial justify-around sm:justify-start">
            {visibleItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex flex-col items-center justify-center px-2 sm:px-3 py-2 rounded-btn
                  min-h-touch min-w-[52px] sm:min-w-[60px] transition-all relative
                  ${isActive
                    ? 'bg-sage-50 text-sage-700'
                    : 'text-morandi-400 hover:text-morandi-600 hover:bg-morandi-50'
                  }
                `}
              >
                <span className="text-lg leading-none mb-0.5">{item.icon}</span>
                <span className="text-xs">{item.label}</span>
                {item.badge != null && item.badge > 0 && (
                  <span className="absolute -top-0.5 right-1 w-5 h-5 bg-rose-400 text-white text-xs rounded-full flex items-center justify-center font-mono">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </NavLink>
            ))}

            {moreItems.length > 0 && (
              <button
                onClick={() => setMoreMenu(true)}
                className="flex flex-col items-center justify-center px-2 sm:px-3 py-2 rounded-btn min-h-touch min-w-[52px] sm:min-w-[60px] text-morandi-400 hover:text-morandi-600 hover:bg-morandi-50"
              >
                <span className="text-lg leading-none mb-0.5">⋯</span>
                <span className="text-xs">更多</span>
              </button>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}
